import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, BookOpen, Info } from "lucide-react";
import { toast } from "sonner";

const englishCourses = [
  {
    id: "bc-learnenglish",
    title: "British Council – LearnEnglish",
    provider: "British Council",
    level: "All levels",
    outcome: "Practice speaking, listening, grammar & vocabulary.",
    url: "https://learnenglish.britishcouncil.org/",
    tags: ["english", "communication", "speaking", "grammar"],
  },
  {
    id: "bc-courses",
    title: "British Council – Free Online Courses",
    provider: "British Council",
    level: "All levels",
    outcome: "Short guided courses to improve English for study/work.",
    url: "https://www.britishcouncil.org/english/learn-online/courses",
    tags: ["english", "communication", "workplace"],
  },
  {
    id: "usalearns",
    title: "USA Learns – Free Online English Courses",
    provider: "USA Learns",
    level: "Beginner to Intermediate",
    outcome: "Structured lessons for everyday English.",
    url: "https://www.usalearns.org/free-online-english-courses",
    tags: ["english", "beginner", "everyday"],
  },
  {
    id: "cambridge",
    title: "Cambridge English – Activities for Learners",
    provider: "Cambridge English",
    level: "By level",
    outcome: "Quick practice activities across skills.",
    url: "https://www.cambridgeenglish.org/learning-english/activities-for-learners",
    tags: ["english", "cambridge", "practice"],
  },
  {
    id: "swayam-english",
    title: "SWAYAM – English Communication",
    provider: "SWAYAM (Govt of India)",
    level: "All levels",
    outcome: "Communication skills aligned for students.",
    url: "https://onlinecourses.swayam2.ac.in/cec26_hs08/preview",
    tags: ["english", "communication", "swayam", "government"],
    isIndian: true,
  },
  {
    id: "nptel-english",
    title: "NPTEL – English for Competitive Exams",
    provider: "NPTEL",
    level: "Intermediate+",
    outcome: "Improve language for exams and job readiness.",
    url: "https://onlinecourses.nptel.ac.in/noc26_hs74/preview",
    tags: ["english", "nptel", "competitive", "employability"],
    isIndian: true,
  },
  {
    id: "openlearn-everyday",
    title: "OpenLearn – Everyday English 1",
    provider: "OpenLearn (Open University)",
    level: "Beginner",
    outcome: "Foundations of everyday speaking & listening.",
    url: "https://www.open.edu/openlearn/languages/everyday-english-1/",
    tags: ["english", "beginner", "speaking", "listening"],
  },
  {
    id: "openlearn-skills",
    title: "OpenLearn – English: Skills for Learning",
    provider: "OpenLearn (Open University)",
    level: "Beginner to Intermediate",
    outcome: "Reading & writing skills for learning and work.",
    url: "https://www.open.edu/openlearn/education-development/english-skills-learning",
    tags: ["english", "reading", "writing", "workplace"],
  },
  {
    id: "bbc-english",
    title: "BBC Learning English",
    provider: "BBC",
    level: "All levels",
    outcome: "Short lessons and videos for daily learning.",
    url: "https://www.bbc.co.uk/learningenglish",
    tags: ["english", "bbc", "video", "daily"],
    secondaryUrl: "https://www.youtube.com/user/bbclearningenglish",
    secondaryLabel: "YouTube lessons",
  },
];

export const FreeEnglishSection = () => {
  const handleOpen = (url: string, title: string) => {
    toast.info("Opening official resource...", {
      description: `Redirecting to ${title}.`,
    });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="free-english" className="py-16 md:py-24 bg-background">
      <div className="container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <Badge variant="free" className="mb-4">
            <BookOpen className="h-3 w-3 mr-1" />
            Career Enabler — Free Access
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            English Can Open More Doors
          </h2>
          <p className="text-muted-foreground text-lg">
            Strong English communication skills may improve your interview confidence and career prospects. Start learning free today.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {englishCourses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.07 }}
              className="p-5 rounded-2xl bg-card border border-border hover:shadow-card hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              {/* Badges row */}
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="free">FREE</Badge>
                <Badge variant="online">Online</Badge>
                {course.isIndian && (
                  <Badge variant="sector">🇮🇳 India</Badge>
                )}
                {!course.isIndian && (
                  <Badge variant="outline">🌍 Global</Badge>
                )}
              </div>

              {/* Title */}
              <h3 className="text-base font-semibold text-foreground mb-1 leading-snug">
                {course.title}
              </h3>

              {/* Provider & Level */}
              <p className="text-xs text-primary font-medium mb-1">{course.provider}</p>
              <p className="text-xs text-muted-foreground mb-3">
                <span className="font-medium">Level:</span> {course.level}
              </p>

              {/* Outcome */}
              <p className="text-sm text-muted-foreground mb-4 flex-1">{course.outcome}</p>

              {/* Actions */}
              <div className="flex flex-col gap-2 mt-auto">
                <Button
                  variant="hero"
                  size="sm"
                  className="w-full"
                  onClick={() => handleOpen(course.url, course.title)}
                >
                  Start Learning
                  <ExternalLink className="h-3 w-3" />
                </Button>
                {course.secondaryUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleOpen(course.secondaryUrl!, course.secondaryLabel!)}
                  >
                    {course.secondaryLabel}
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Disclaimer note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded-xl p-4 max-w-2xl mx-auto text-center justify-center"
        >
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
          <span>
            Links are to official providers. Some platforms offer optional paid certificates—learning access remains free.
          </span>
        </motion.div>
      </div>
    </section>
  );
};
